import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Crown, Check } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEMBERSHIP_TIERS = [
  {
    level: 3,
    name: 'Level 3',
    price: 4,
    days: 30,
    features: ['Unlimited Chat', 'Access to All Rooms', 'Drawing Board', 'Play Games']
  },
  {
    level: 2,
    name: 'Level 2',
    price: 12,
    days: 150,
    features: ['Everything in Level 3', 'Priority Support', 'Exclusive Rooms', 'Advanced Features', 'Early Access']
  },
  {
    level: 1,
    name: 'Level 1 - Elite',
    price: 20,
    days: 250,
    features: ['Everything in Level 2', 'VIP Badge', 'Custom Username Color', 'Ad-Free Experience', 'Lifetime Updates']
  }
];

export function MembershipPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handlePayment = async (tier) => {
    if (!user) {
      toast.error('Please login to subscribe');
      return;
    }

    setLoading(true);
    try {
      const amount = tier.price * 100;
      const { data: order } = await axios.post(
        `${API}/payment/create-order`,
        { amount, currency: 'INR', membership_level: tier.level },
        { withCredentials: true }
      );

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_dummy',
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: 'TruthConfession',
        description: `${tier.name} Membership`,
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/payment/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                membership_level: tier.level
              },
              { withCredentials: true }
            );
            toast.success('Payment successful! Your membership is now active.');
            window.location.href = '/members';
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: '#E11D48'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Payment initialization failed. Please note: Razorpay integration requires valid API keys.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-16 md:py-24 px-4 sm:px-6" data-testid="membership-page">
      <div className="w-full max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground mb-4">UNLOCK PREMIUM FEATURES</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-none font-light mb-4 md:mb-6" data-testid="membership-heading">
            Join Our <span className="text-primary">Elite</span> Members
          </h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            Choose the perfect plan for unlimited access to all features
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {MEMBERSHIP_TIERS.map((tier, index) => (
            <div
              key={tier.level}
              className={`bg-card border p-8 hover:-translate-y-1 transition-transform duration-300 ${
                tier.level === 1 ? 'border-primary shadow-[0_0_30px_rgba(225,29,72,0.3)]' : 'border-border'
              }`}
              data-testid={`membership-tier-${tier.level}`}
            >
              {tier.level === 1 && (
                <div className="flex items-center justify-center mb-4">
                  <Crown size={32} weight="fill" className="text-primary" />
                </div>
              )}
              
              <h3 className="text-2xl tracking-tight font-light text-center mb-2" data-testid={`tier-${tier.level}-name`}>
                {tier.name}
              </h3>
              
              <div className="text-center mb-6">
                <span className="text-5xl font-light" data-testid={`tier-${tier.level}-price`}>${tier.price}</span>
                <span className="text-muted-foreground text-sm"> / {tier.days} days</span>
              </div>

              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm" data-testid={`tier-${tier.level}-feature-${idx}`}>
                    <Check size={20} weight="bold" className="text-primary mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handlePayment(tier)}
                disabled={loading || (user && user.membership_level > 0 && user.membership_level <= tier.level)}
                className={`w-full ${
                  tier.level === 1 ? 'bg-primary hover:bg-primary/90' : 'bg-secondary hover:bg-secondary/80'
                } rounded-full`}
                data-testid={`tier-${tier.level}-subscribe-button`}
              >
                {user && user.membership_level > 0 && user.membership_level <= tier.level
                  ? 'Current Plan'
                  : 'Subscribe Now'}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>Note: Payment integration requires Razorpay API keys to be configured.</p>
        </div>
      </div>
    </div>
  );
}